/**
 * GameTableArea — Main game table rendering area with sport-specific table components.
 *
 * Renders moneyline (consolidated or two-row), totals, or spreads table
 * based on the selected market type. Includes the right-side game detail panel.
 */

import type { ConsolidatedGameBooks } from '@/lib/nbaConsolidated/types';
import type {
  GameData,
  SportsLoadingState,
  TotalGameData,
  SpreadGameData,
} from '@/lib/sportsStream';
import type { OddsMode } from './DashboardHeader';
import type { TradeClickRequest } from './tradeTypes';
import type { VolumeWindow } from '@/lib/nbaConsolidated/volumeSettings';
import type { Sport } from '@/lib/sportsDiscovery';
import { ConsolidatedMoneylines } from './ConsolidatedMoneylines';
import { TwoRowMoneylines } from './TwoRowMoneylines';
import { GameDetailPanel } from './GameDetailPanel';
import { TwoRowTotals, TwoRowSpreads } from '@/components/sports';
import type { ViewMode } from './DashboardHeader';
import type { MarketType } from '@/lib/sportsDiscovery';

interface GameTableAreaProps {
  /** Which market type is selected */
  selectedMarketType: MarketType;
  /** Whether moneyline view is active */
  useMoneylineView: boolean;
  /** View mode: consolidated or two-row */
  viewMode: ViewMode;
  /** Selected sport */
  sport: Sport;
  /** Odds mode: maker or taker */
  oddsMode: OddsMode;
  /** Moneyline games filtered for current view */
  moneylineGamesForView: ConsolidatedGameBooks[];
  /** Whether moneyline data is loading */
  moneylineLoading: boolean;
  /** Loading state for current sport */
  currentLoadingState?: SportsLoadingState;
  /** Max liquidity for bar scaling */
  maxLiquidity: number;
  /** Currently expanded game keys */
  expandedGameKeys: Set<string>;
  /** Toggle game expansion */
  onToggleExpand: (gameKey: string) => void;
  /** Trade click handler */
  onTradeClick: (req: TradeClickRequest) => void;
  /** Navigate to market (toggle expansion) */
  onNavigateToMarket: (marketTicker: string) => void;
  /** Table collapsed state */
  tableCollapsed: boolean;
  onToggleCollapsed: () => void;
  /** Table compact state */
  tableCompact: boolean;
  onToggleCompact: () => void;
  /** Filtered current games (for spread/total views) */
  filteredCurrentGames: GameData[];
  /** Whether current sport data is loading */
  currentLoading: boolean;
  /** Volume windows for spread/total views */
  volumeWindows: VolumeWindow[];
  /** Selected game for detail panel */
  selectedGame: ConsolidatedGameBooks | null;
  /** Close detail panel */
  onCloseDetailPanel: () => void;
}

export function GameTableArea({
  selectedMarketType,
  useMoneylineView,
  viewMode,
  sport,
  oddsMode,
  moneylineGamesForView,
  moneylineLoading,
  currentLoadingState,
  maxLiquidity,
  expandedGameKeys,
  onToggleExpand,
  onTradeClick,
  onNavigateToMarket,
  tableCollapsed,
  onToggleCollapsed,
  tableCompact,
  onToggleCompact,
  filteredCurrentGames,
  currentLoading,
  volumeWindows,
  selectedGame,
  onCloseDetailPanel,
}: GameTableAreaProps) {
  return (
    <div className="flex gap-4">
      {/* Table area (grows to fill available space) */}
      <div className="min-w-0 flex-1">
        {/* Moneyline View */}
        {useMoneylineView &&
          (viewMode === 'two-row' ? (
            <TwoRowMoneylines
              games={moneylineGamesForView}
              isLoading={moneylineLoading}
              loadingState={currentLoadingState}
              oddsMode={oddsMode}
              maxLiquidity={maxLiquidity}
              expandedGameKeys={expandedGameKeys}
              onToggleExpand={onToggleExpand}
              onTradeClick={onTradeClick}
              onNavigateToMarket={onNavigateToMarket}
              sport={sport}
              collapsed={tableCollapsed}
              onToggleCollapsed={onToggleCollapsed}
              compact={tableCompact}
              onToggleCompact={onToggleCompact}
            />
          ) : (
            <ConsolidatedMoneylines
              games={moneylineGamesForView}
              isLoading={moneylineLoading}
              loadingState={currentLoadingState}
              oddsMode={oddsMode}
              maxLiquidity={maxLiquidity}
              expandedGameKeys={expandedGameKeys}
              onToggleExpand={onToggleExpand}
              onTradeClick={onTradeClick}
              onNavigateToMarket={onNavigateToMarket}
              sport={sport}
              collapsed={tableCollapsed}
              onToggleCollapsed={onToggleCollapsed}
              compact={tableCompact}
              onToggleCompact={onToggleCompact}
            />
          ))}

        {/* Totals view */}
        {selectedMarketType === 'total' && (
          <TwoRowTotals
            sport={sport}
            games={filteredCurrentGames as TotalGameData[]}
            isLoading={currentLoading}
            loadingState={currentLoadingState}
            volumeWindows={volumeWindows}
            onTradeClick={onTradeClick}
            onNavigateToMarket={onNavigateToMarket}
          />
        )}

        {/* Spreads view */}
        {selectedMarketType === 'spread' && (
          <TwoRowSpreads
            sport={sport}
            games={filteredCurrentGames as SpreadGameData[]}
            isLoading={currentLoading}
            loadingState={currentLoadingState}
            volumeWindows={volumeWindows}
            onTradeClick={onTradeClick}
            onNavigateToMarket={onNavigateToMarket}
          />
        )}
      </div>

      {/* Right-side game detail panel */}
      {selectedGame && useMoneylineView && (
        <GameDetailPanel
          game={selectedGame}
          oddsMode={oddsMode}
          onTradeClick={onTradeClick}
          onClose={onCloseDetailPanel}
        />
      )}
    </div>
  );
}
